import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle, Radio, Users, FileText, BookOpen, ArrowRight, Sparkles, Lightbulb,
} from "lucide-react";
import { api } from "@/lib/api";

const NAVY = "#0E1D2D";
const GOLD = "#C9953D";
const BORDER = "#E7E5E2";

const ICON_MAP = { AlertTriangle, Radio, Users, FileText, BookOpen, Sparkles };

const TONE_STYLE = {
  warning:  { badge: "AÇÃO",       bg: "#FEF2F2", color: "#B91C1C", ring: "#FECACA" },
  info:     { badge: "INFO",       bg: "#EFF6FF", color: "#1E40AF", ring: "#BFDBFE" },
  neutral:  { badge: "REVISAR",    bg: "#F1F5F9", color: "#334155", ring: "#CBD5E1" },
  positive: { badge: "PROGRESSO",  bg: "#F0FDF4", color: "#166534", ring: "#BBF7D0" },
};

export default function InsightsJuridicos() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/dashboard/insights-juridicos");
        setItems(r.data?.items || []);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="bg-white rounded-2xl border p-5" style={{ borderColor: BORDER }}
         data-testid="insights-juridicos">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center"
               style={{ background: `${GOLD}22`, color: GOLD, border: `1px solid ${GOLD}55` }}>
            <Lightbulb className="w-3.5 h-3.5"/>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">
              HERMES RECOMENDA
            </div>
            <div className="font-serif text-base text-[#132238] leading-tight">
              Insights jurídicos
            </div>
          </div>
        </div>
        <span className="text-[10px] text-slate-400" title="Recalculado a cada acesso">
          real-time
        </span>
      </div>

      {loading ? (
        <div className="text-xs text-slate-400 py-8 text-center" data-testid="insights-loading">
          Analisando seu escritório…
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-8" data-testid="insights-empty">
          <Sparkles className="w-6 h-6 mx-auto text-emerald-500 mb-2"/>
          <div className="text-xs text-slate-500">
            Nada exige sua ação agora. Bom trabalho!
          </div>
        </div>
      ) : (
        <ul className="space-y-2.5" data-testid="insights-list">
          {items.map((it) => {
            const Icon = ICON_MAP[it.icon] || Sparkles;
            const style = TONE_STYLE[it.tone] || TONE_STYLE.info;
            return (
              <li key={it.id} className="rounded-xl p-3 flex gap-3 items-start transition hover:shadow-sm"
                  style={{ background: style.bg, border: `1px solid ${style.ring}` }}
                  data-testid={`insight-${it.id}`}>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                     style={{ background: "#fff", color: style.color }}>
                  <Icon className="w-4 h-4"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-[9.5px] font-bold tracking-widest px-1.5 py-0.5 rounded"
                          style={{ background: style.color, color: "#fff" }}>
                      {style.badge}
                    </span>
                  </div>
                  <div className="text-[13px] font-semibold text-[#132238] leading-snug">
                    {it.title}
                  </div>
                  <div className="text-[11.5px] text-slate-600 leading-relaxed mt-0.5">
                    {it.detail}
                  </div>
                  {it.cta && (
                    <Link to={it.cta.to}
                          data-testid={`insight-cta-${it.id}`}
                          className="mt-2 inline-flex items-center gap-1 text-[11.5px] font-semibold hover:underline"
                          style={{ color: style.color }}>
                      {it.cta.label} <ArrowRight className="w-3 h-3"/>
                    </Link>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
